import type { AnalysisApi } from "@amtana/analysis-contract";
import { analysisSchema, modeSchema, progressSchema, statusSchema } from "@amtana/analysis-contract";
import type { ListApi } from "@amtana/list-contract";
import { addListProspectsInputSchema, filterListSchema } from "@amtana/list-contract";
import type { ProspectApi } from "@amtana/prospect-contract";
import { prospectSchema } from "@amtana/prospect-contract";
import { z } from "zod";

// Every action an agent can take, declared once (ADR-023 §3): its name, what it does in words an
// agent reads to pick it, its input and output, the permission it needs and the module
// operations it calls, each through that module's `*Api`. The agent doors serve this list
// (`GET /api/agents/me/tools`) and run one tool by name (`POST /api/agents/me/tools/{name}`);
// the deferred MCP door would serve the same list. Prospecting tools only: reading and starting
// analyses, searching prospects, adding them to a list with notes, reading lists. No tool sends
// a message, a connection request or a follow (D552); outreach stays in campaigns, where a person
// approves it, and `agent.tools.unit.test.ts` fails on a tool whose name or words suggest one.

type OperationsOf<Module extends string, Api> = `${Module}.${Extract<keyof Api, string>}`;

/** A module operation a tool calls: `<module>.<method>` of that module's `*Api`, checked by type. */
export type AgentToolOperation =
  | OperationsOf<"analysis", AnalysisApi>
  | OperationsOf<"prospect", ProspectApi>
  | OperationsOf<"list", ListApi>;

export type AgentTool<TInput extends z.ZodType = z.ZodType, TOutput extends z.ZodType = z.ZodType> = {
  /** `[a-z]+_[a-z]+`: what MCP accepts too. */
  readonly name: string;
  readonly description: string;
  /** A permission of `@amtana/access`, checked against the owner's role cut to the agent's list. */
  readonly permission: string;
  readonly input: TInput;
  readonly output: TOutput;
  /** Every operation a run may call; a run calls no other (agent-tools.service.unit.test.ts). */
  readonly calls: readonly AgentToolOperation[];
};

/** What a tool's run receives once its input passed the schema. */
export type AgentToolInput<T extends AgentTool> = z.output<T["input"]>;

function tool<TInput extends z.ZodType, TOutput extends z.ZodType>(
  declaration: AgentTool<TInput, TOutput>,
): AgentTool<TInput, TOutput> {
  return Object.freeze(declaration);
}

// What a tool answers: the fields an agent decides on, never the whole record (fewer tokens, and
// nothing it has no use for).

export const agentAnalysisSchema = analysisSchema
  .pick({ id: true, name: true, platform: true, mode: true, target: true, status: true })
  .extend({
    progress: progressSchema.pick({
      profilesAnalyzed: true,
      publicAccounts: true,
      emails: true,
      phones: true,
      completion: true,
    }),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
  });
export type AgentAnalysis = z.infer<typeof agentAnalysisSchema>;

export const agentProspectSchema = prospectSchema.pick({
  id: true,
  platform: true,
  name: true,
  username: true,
  email: true,
  phone: true,
  website: true,
  followers: true,
  biography: true,
  country: true,
  niche: true,
  isBusiness: true,
  qualification: true,
  doNotContact: true,
});
export type AgentProspect = z.infer<typeof agentProspectSchema>;

export const agentListSchema = filterListSchema
  .pick({ id: true, name: true, description: true, status: true, prospectCount: true, emailCount: true })
  .extend({
    /** True for a picked list: prospects added by hand, with notes (D603). */
    picked: z.boolean(),
    updatedAt: z.string(),
  });
export type AgentList = z.infer<typeof agentListSchema>;

const id = z.string().trim().min(1).max(64);
const limit = (fallback: number, max: number) => z.number().int().min(1).max(max).default(fallback);

export const analysisListTool = tool({
  name: "analysis_list",
  description:
    "List the analyses of the current project, newest first, with each one's status and progress. Narrow by status.",
  permission: "analysis:read",
  input: z.object({ status: statusSchema.optional(), limit: limit(20, 50) }).strict(),
  output: z.object({
    analyses: z.array(agentAnalysisSchema),
    counts: z.record(z.string(), z.number().int().nonnegative()),
  }),
  calls: ["analysis.list"],
});

export const analysisGetTool = tool({
  name: "analysis_get",
  description:
    "Read one analysis and a page of the prospects it found, optionally only those with an email address.",
  permission: "analysis:read",
  input: z
    .object({
      analysisId: id,
      page: z.number().int().min(1).default(1),
      pageSize: limit(20, 100),
      hasEmail: z.boolean().optional(),
    })
    .strict(),
  output: z.object({
    analysis: agentAnalysisSchema,
    results: z.object({
      prospects: z.array(agentProspectSchema),
      total: z.number().int().nonnegative(),
      page: z.number().int().positive(),
      pageSize: z.number().int().positive(),
    }),
  }),
  calls: ["analysis.get", "prospect.list"],
});

export const analysisStartTool = tool({
  name: "analysis_start",
  description:
    "Start an analysis: one already set up, by its id, or a new one from a mode and a search, on the first target the search finds. Only the two Instagram account modes a provider reads can run today; any other mode is refused. It spends the organization's credits as it runs.",
  permission: "analysis:write",
  input: z.union([
    z.object({ analysisId: id }).strict(),
    z
      .object({
        mode: modeSchema,
        query: z.string().trim().min(1).max(120),
        area: z.string().trim().max(120).optional(),
        name: z.string().trim().min(1).max(80).optional(),
      })
      .strict(),
  ]),
  output: z.object({ analysis: agentAnalysisSchema }),
  calls: [
    "analysis.start",
    "analysis.searchTargets",
    "analysis.create",
    "analysis.updateWizard",
    "analysis.rename",
  ],
});

export const prospectSearchTool = tool({
  name: "prospect_search",
  description:
    "Search the organization's prospects by free words found in their name, username, biography, niche or country, best match first; optionally within one analysis, or only those with an email address.",
  permission: "prospect:read",
  input: z
    .object({
      query: z.string().trim().max(200).optional(),
      analysisId: id.optional(),
      hasEmail: z.boolean().optional(),
      page: z.number().int().min(1).default(1),
      limit: limit(20, 100),
    })
    .strict(),
  output: z.object({ prospects: z.array(agentProspectSchema), total: z.number().int().nonnegative() }),
  calls: ["prospect.list"],
});

export const prospectAddTool = tool({
  name: "prospect_add",
  description:
    "Add prospects to a list by the list's name, each with a note saying why it fits; the list is made when none has the name. A list made by its rules cannot take prospects by hand.",
  permission: "list:write",
  input: addListProspectsInputSchema.strict(),
  output: z.object({
    list: agentListSchema,
    added: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
  }),
  calls: ["list.addProspects"],
});

export const listListTool = tool({
  name: "list_list",
  description: "List the current project's lists with their size, newest change first.",
  permission: "list:read",
  input: z.object({}).strict(),
  output: z.object({ lists: z.array(agentListSchema) }),
  calls: ["list.list"],
});

export const listGetTool = tool({
  name: "list_get",
  description:
    "Read one list by its id or its name, with up to 100 of its prospects and, on a picked list, the note each was added with.",
  permission: "list:read",
  input: z.object({ list: z.string().trim().min(1).max(80), limit: limit(50, 100) }).strict(),
  output: z.object({
    list: agentListSchema,
    prospects: z.array(agentProspectSchema.extend({ note: z.string().nullable() })),
  }),
  calls: ["list.list", "prospect.inScope"],
});

/** The tool list, in the order an agent reads it. */
export const AGENT_TOOLS = [
  analysisListTool,
  analysisGetTool,
  analysisStartTool,
  prospectSearchTool,
  prospectAddTool,
  listListTool,
  listGetTool,
] as const;

export type AgentToolName = (typeof AGENT_TOOLS)[number]["name"];

/** The tool of this name, or undefined. */
export function agentToolNamed(name: string): AgentTool | undefined {
  return AGENT_TOOLS.find((candidate) => candidate.name === name);
}

/** One tool as `GET /api/agents/me/tools` shows it: its input as a JSON Schema, and whether this
 * agent may run it now (its owner's role cut to its own list). */
export const agentToolViewSchema = z.object({
  name: z.string(),
  description: z.string(),
  permission: z.string(),
  allowed: z.boolean(),
  input: z.record(z.string(), z.unknown()),
});
export type AgentToolView = z.infer<typeof agentToolViewSchema>;

export const agentToolListSchema = z.object({ tools: z.array(agentToolViewSchema) });
export type AgentToolList = z.infer<typeof agentToolListSchema>;

/** `POST /api/agents/me/tools/{name}`: the tool's name from the path, its input as the body. */
export const runAgentToolInputSchema = z.object({ name: z.string().min(1).max(64), body: z.unknown() });
